import { CloseTemplateBasis } from "@common/schemas/close-template.schema";
import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplateAssigneeRole";
import {
    CloseTemplatesSortValue,
    CloseTemplatesViewColumn,
} from "@common/types/views/closeTemplatesView";
import { ViewConfig } from "@lib/views";
import { CloseTemplateActionsCell } from "./cells/CloseTemplateActionsCell";
import { CloseTemplateApplicationCountCell } from "./cells/CloseTemplateApplicationCountCell";
import { CloseTemplateBasisCell } from "./cells/CloseTemplateBasisCell";
import { CloseTemplateClientCell } from "./cells/CloseTemplateClientCell";
import { CloseTemplateEstimatedTimeCell } from "./cells/CloseTemplateEstimatedTimeCell";
import { CloseTemplateLastAppliedCell } from "./cells/CloseTemplateLastAppliedCell";
import { CloseTemplateNameCell } from "./cells/CloseTemplateNameCell";
import { CloseTemplateOwnerCell } from "./cells/CloseTemplateOwnerCell";
import { CloseTemplateScheduleCountCell } from "./cells/CloseTemplateScheduleCountCell";
import { CloseTemplateServiceCell } from "./cells/CloseTemplateServiceCell";
import { CloseTemplateTaskCountCell } from "./cells/CloseTemplateTaskCountCell";
import { CloseTemplateUpdatedCell } from "./cells/CloseTemplateUpdatedCell";
import { CloseTemplateUpdatedByCell } from "./cells/CloseTemplateUpdatedByCell";

export interface CloseTemplateRow {
    id: string;
    name: string;
    organizationId: string;
    workspaceId: string | null;
    serviceId: string | null;
    ownerId: number | null;
    createdByUserId: number | null;
    updatedByUserId: number | null;
    basis: CloseTemplateBasis | null;
    industries: EntityProfileIndustry[];
    entityLegalTypes: EntityProfileType[];
    updatedAt: Date;
    applicationCount: number;
    lastAppliedAt: Date | null;
    taskCount: number;
    scheduleCount: number;
    totalEstimatedTime: number;
    estimatedTimeByRole: Partial<Record<TaskTemplateAssigneeRole, number>>;
    archivedAt: Date | null;
}

export const organizationCloseTemplatesViewConfig: ViewConfig<
    CloseTemplateRow,
    CloseTemplatesViewColumn,
    CloseTemplatesSortValue
> = {
    columns: {
        [CloseTemplatesViewColumn.NAME]: {
            accessorKey: "name",
            name: "Service",
            header: "Service",
            cell: CloseTemplateNameCell,
            sort: {
                asc: CloseTemplatesSortValue.NAME_ASC,
                desc: CloseTemplatesSortValue.NAME_DESC,
                defaultOrder: "asc",
            },
            // Pinned left by default and can't be unpinned/hidden/reordered —
            // primary identity column for the templates list.
            immutable: true,
            minSize: 220,
            maxSize: 1200,
        },
        [CloseTemplatesViewColumn.SERVICE]: {
            accessorKey: "serviceId",
            name: "Tier",
            header: "Tier",
            cell: CloseTemplateServiceCell,
            minSize: 140,
            maxSize: 240,
        },
        [CloseTemplatesViewColumn.CLIENT]: {
            accessorKey: "workspaceId",
            name: "Client",
            header: "Client",
            cell: CloseTemplateClientCell,
            minSize: 160,
            maxSize: 300,
        },
        [CloseTemplatesViewColumn.OWNER]: {
            accessorKey: "ownerId",
            name: "Owner",
            header: "Owner",
            cell: CloseTemplateOwnerCell,
            sort: {
                asc: CloseTemplatesSortValue.OWNER_ASC,
                desc: CloseTemplatesSortValue.OWNER_DESC,
            },
            minSize: 160,
            maxSize: 240,
        },
        [CloseTemplatesViewColumn.BASIS]: {
            accessorKey: "basis",
            name: "Basis",
            header: "Basis",
            cell: CloseTemplateBasisCell,
            minSize: 140,
            maxSize: 200,
        },
        [CloseTemplatesViewColumn.UPDATED_BY]: {
            accessorKey: "updatedByUserId",
            name: "Updated by",
            header: "Updated by",
            cell: CloseTemplateUpdatedByCell,
            minSize: 160,
            maxSize: 240,
        },
        [CloseTemplatesViewColumn.UPDATED]: {
            accessorKey: "updatedAt",
            name: "Updated on",
            header: "Updated on",
            cell: CloseTemplateUpdatedCell,
            sort: {
                asc: CloseTemplatesSortValue.UPDATED_AT_ASC,
                desc: CloseTemplatesSortValue.UPDATED_AT_DESC,
            },
            minSize: 140,
            maxSize: 180,
        },
        [CloseTemplatesViewColumn.NUMBER_OF_APPLICATIONS]: {
            accessorKey: "applicationCount",
            name: "Applications",
            header: "Applications",
            cell: CloseTemplateApplicationCountCell,
            sort: {
                asc: CloseTemplatesSortValue.APPLICATIONS_ASC,
                desc: CloseTemplatesSortValue.APPLICATIONS_DESC,
            },
            align: "right",
            minSize: 120,
            maxSize: 180,
        },
        [CloseTemplatesViewColumn.LAST_APPLIED]: {
            accessorKey: "lastAppliedAt",
            name: "Last applied",
            header: "Last applied",
            cell: CloseTemplateLastAppliedCell,
            sort: {
                asc: CloseTemplatesSortValue.LAST_APPLIED_ASC,
                desc: CloseTemplatesSortValue.LAST_APPLIED_DESC,
            },
            minSize: 140,
            maxSize: 200,
        },
        [CloseTemplatesViewColumn.NUMBER_OF_TASKS]: {
            accessorKey: "taskCount",
            name: "Tasks",
            header: "Tasks",
            cell: CloseTemplateTaskCountCell,
            sort: {
                asc: CloseTemplatesSortValue.TASKS_ASC,
                desc: CloseTemplatesSortValue.TASKS_DESC,
            },
            align: "right",
            minSize: 100,
            maxSize: 160,
        },
        [CloseTemplatesViewColumn.NUMBER_OF_SCHEDULES]: {
            accessorKey: "scheduleCount",
            name: "Schedules",
            header: "Schedules",
            cell: CloseTemplateScheduleCountCell,
            sort: {
                asc: CloseTemplatesSortValue.SCHEDULES_ASC,
                desc: CloseTemplatesSortValue.SCHEDULES_DESC,
            },
            align: "right",
            minSize: 120,
            maxSize: 180,
        },
        [CloseTemplatesViewColumn.ESTIMATED_TIME]: {
            accessorKey: "totalEstimatedTime",
            name: "Estimated time",
            header: "Estimated time",
            cell: CloseTemplateEstimatedTimeCell,
            sort: {
                asc: CloseTemplatesSortValue.ESTIMATED_TIME_ASC,
                desc: CloseTemplatesSortValue.ESTIMATED_TIME_DESC,
            },
            align: "right",
            minSize: 140,
            maxSize: 200,
        },
        [CloseTemplatesViewColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: CloseTemplateActionsCell,
            minSize: 120,
            maxSize: 120,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
};
